/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Heart, Download, Check, Edit3 } from 'lucide-react';
import { ShishaMix } from '../types';
import sharecardBg from '../assets/sharecard.png';

interface MixDetailModalProps {
  mix: ShishaMix;
  onClose: () => void;
  onLike: (mixId: string) => void;
  onBookmark: (mixId: string) => void;
  onAddComment: (mixId: string, commentText: string) => void;
  isLiked: boolean;
  isBookmarked: boolean;
  onFavorite?: (mixId: string) => void;
  isFavorite?: boolean;
  onDelete?: (mixId: string) => void;
}

export default function MixDetailModal({
  mix,
  onClose,
  onFavorite,
  isFavorite = false,
  onDelete,
}: MixDetailModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const navigate = useNavigate();

  // Responsive scale
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // 800px is the card width, 40px is for some margin
      if (width < 840) {
        setScale((width - 40) / 800);
      } else {
        setScale(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 文字列の視覚的な長さを計算するヘルパー（全角=1、半角=0.55）
  const getVisualLength = (str: string) => {
    let length = 0;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      if ((c >= 0x0 && c < 0x81) || (c === 0xf8f0) || (c >= 0xff61 && c < 0xffa0) || (c >= 0xf8f1 && c < 0xf8f4)) {
        length += 0.55;
      } else {
        length += 1;
      }
    }
    return length;
  };

  // タイトルが690px枠（28pxで約24文字）に収まるようにフォントサイズを動的に計算
  const titleVisualLength = getVisualLength(mix.title);
  const maxChars = 24;
  let titleFontSize = 28;
  if (titleVisualLength > maxChars) {
    titleFontSize = Math.max(12, Math.floor(28 * (maxChars / titleVisualLength)));
  }

  // QRコードをレシピ名+フレーバー+分量比のJSON形式で生成
  useEffect(() => {
    if (!qrCanvasRef.current) return;
    const canvas = qrCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Payload: flavors only (name omitted to reduce QR data volume)
    const qrPayload = JSON.stringify({
      flavors: mix.flavors.map((fl) => ({
        brand_name: fl.brand,
        flavor_name: fl.name,
        ratio: fl.ratio,
      })),
    });

    import('../utils/qrCodeUtils').then(({ generateQrPlain }) => {
      generateQrPlain(qrPayload).then((dataUrl) => {
        const qrImg = new Image();
        qrImg.onload = () => {
          canvas.width = qrImg.width;
          canvas.height = qrImg.height;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(qrImg, 0, 0, canvas.width, canvas.height);
        };
        qrImg.src = dataUrl;
      }).catch(console.error);
    });
  }, [mix.id]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleKeyDownBackdrop = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      // Clone the card to render it off-screen at 1x scale
      const captureNode = cardRef.current.cloneNode(true) as HTMLElement;
      captureNode.style.position = 'fixed';
      captureNode.style.left = '-9999px';
      captureNode.style.top = '0';
      captureNode.style.transform = 'none';
      document.body.appendChild(captureNode);

      // Copy canvas contents (QR code) because cloneNode doesn't copy canvas pixels
      const originalCanvases = cardRef.current.querySelectorAll('canvas');
      const clonedCanvases = captureNode.querySelectorAll('canvas');
      originalCanvases.forEach((canvas, i) => {
        const ctx = clonedCanvases[i].getContext('2d');
        if (ctx) ctx.drawImage(canvas, 0, 0);
      });

      await new Promise((resolve) => setTimeout(resolve, 50)); // let DOM settle
      
      // Clean up ripple effects
      captureNode.querySelectorAll('.ripple-effect').forEach((el) => el.remove());

      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(captureNode, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      });

      document.body.removeChild(captureNode);

      const url = canvas.toDataURL('image/png');
      const filename = `hookah-forge-${mix.title.replace(/\\s+/g, '_')}.png`;
      const { saveImageFile } = await import('../utils/imageUtils');
      const success = await saveImageFile(url, filename);
      if (!success) return; // cancelled or failed
      
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to generate PNG', err);
    }
  };



  return (
    <div
      className={`modal-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleClose}
      onKeyDown={handleKeyDownBackdrop}
      role="button"
      tabIndex={0}
      aria-label="モーダルを閉じる"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(20, 22, 25, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      {/* Outer wrapper for buttons */}
      <div 
        style={{ 
          position: 'relative', 
          width: `${800 * scale}px`, 
          height: `${454 * scale}px` 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: '800px', height: '454px' }}>
          {/* The Card */}
          <div
          ref={cardRef}
          style={{
            width: '800px',
            height: '454px',
            backgroundImage: `url(${sharecardBg})`,
            backgroundSize: '100% 100%',
            position: 'relative',
            borderRadius: '24px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            overflow: 'hidden'
          }}
        >
          {/* Recipe Title */}
          <div
            style={{
              position: 'absolute',
              top: '65px',
              left: '54px',
              width: '690px',
              color: '#E7E4DF',
              fontSize: `${titleFontSize}px`,
              fontFamily: 'var(--font-heading-ja)',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.2',
              // 縦位置を中央揃えっぽく微調整（フォントサイズが小さくなった場合）
              display: 'flex',
              alignItems: 'flex-end',
              height: '34px', 
            }}
          >
            {mix.title}
          </div>

          {/* Flavors List */}
          {mix.flavors.slice(0, 6).map((f, i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  position: 'absolute',
                  top: `${202 + i * 38}px`,
                  left: '68px',
                  width: '270px',
                  color: 'rgba(231, 228, 223, 0.9)',
                  fontSize: '15px',
                  fontFamily: 'var(--font-body)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '8px'
                }}
              >
                <span style={{ color: 'rgba(231, 228, 223, 0.6)', fontSize: '13px' }}>{f.brand}</span>
                <span>{f.name}</span>
              </div>
              
              <div
                style={{
                  position: 'absolute',
                  top: `${204 + i * 38}px`,
                  left: '345px',
                  width: '35px',
                  color: '#C9A66B',
                  fontSize: '12px',
                  fontFamily: 'var(--font-numbers)',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  letterSpacing: '1px',
                  lineHeight: '1.2',
                }}
              >
                {f.ratio}
              </div>
            </React.Fragment>
          ))}

          {/* QR Code - レシピ名+フレーバー+分量比のJSONを内蔵 */}
          <div
            style={{
              position: 'absolute',
              top: '182px',
              left: '615px',
              width: '133px',
              height: '133px',
              backgroundColor: '#ffffff',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <canvas
              ref={qrCanvasRef}
              width={121}
              height={121}
              style={{ width: '121px', height: '121px', display: 'block' }}
            />
          </div>
        </div>

        {/* Floating Actions outside the card background */}
        <div style={{ position: 'absolute', top: '-60px', right: '0', display: 'flex', gap: '12px' }}>
          <button
            type="button"
            onClick={handleDownload}
            style={{
              background: 'rgba(231, 228, 223, 0.1)',
              border: '1px solid rgba(231, 228, 223, 0.2)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            title="画像を保存"
          >
            {downloadSuccess ? <Check size={20} color="var(--color-success)" /> : <Download size={20} />}
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/create-recipe', { state: { editMix: mix } })}
            style={{
              background: 'rgba(231, 228, 223, 0.1)',
              border: '1px solid rgba(231, 228, 223, 0.2)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            title="レシピを編集"
          >
            <Edit3 size={20} />
          </button>

          {onFavorite && (
            <button
              type="button"
              onClick={() => onFavorite(mix.id)}
              style={{
                background: isFavorite ? 'rgba(232, 67, 147, 0.15)' : 'rgba(231, 228, 223, 0.1)',
                border: isFavorite ? '1px solid rgba(232, 67, 147, 0.5)' : '1px solid rgba(231, 228, 223, 0.2)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isFavorite ? '#e84393' : 'var(--color-text)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Heart size={20} fill={isFavorite ? '#e84393' : 'none'} />
            </button>
          )}

          {onDelete && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  background: 'rgba(212, 106, 106, 0.1)',
                  border: '1px solid rgba(212, 106, 106, 0.3)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-error)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              >
                <Trash2 size={20} />
              </button>

              {showDeleteConfirm && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50px',
                    right: '0',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid rgba(212, 106, 106, 0.4)',
                    borderRadius: '12px',
                    padding: '16px',
                    zIndex: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '200px',
                  }}
                >
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                    <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>「{mix.title}」</span>
                    <br />を削除しますか？
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '8px',
                        border: '1px solid rgba(231, 228, 223, 0.15)',
                        backgroundColor: 'transparent',
                        color: 'var(--color-text)',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      やめる
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(mix.id);
                        handleClose();
                      }}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: 'var(--color-error)',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      削除する
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleClose}
            style={{
              background: 'rgba(231, 228, 223, 0.1)',
              border: '1px solid rgba(231, 228, 223, 0.2)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text)',
              cursor: 'pointer',
            }}
          >
            <X size={20} />
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
